<div class="min-h-screen overflow-x-hidden relative tfr-payment-page">

  <div class="pointer-events-none absolute inset-x-0 top-0 h-40 tfr-payment-fade"></div>

  <div class="tfr-page-shell-narrow relative z-10 flex max-w-2xl items-center gap-3 pt-5 pb-3">
    <button type="button" (click)="goBack()"
      aria-label="Volver a la selección de hueco"
      class="flex items-center justify-center h-10 w-10 rounded-xl border transition-colors focus:outline-none border-platinum-tint text-slate-blue bg-snow-white/90">
      <lucide-icon name="arrow-left" [size]="17" />
    </button>
    <div class="flex-1 min-w-0">
      <p class="text-[10px] uppercase tracking-[0.16em] font-semibold text-steel-gray">Paso 1 · Checkout</p>
      <h1 class="text-[20px] font-bold tracking-tight leading-tight mt-0.5 text-midnight-indigo">
        Confirmar tu reserva
      </h1>
      <p class="text-[11px] mt-1 leading-none text-steel-gray">
        @if (isStripeCheckout()) {
          Pago seguro del alquiler completo
        } @else {
          Pago de la tasa de gestión online
        }
      </p>
    </div>
  </div>

  <div class="tfr-page-shell-narrow relative z-10 mx-auto flex max-w-2xl flex-col gap-5 py-5 pb-28 lg:pb-8">

    @if (loading()) {
      <div class="bg-white rounded-2xl border p-8 text-center border-pale-gray">
        <lucide-icon name="loader-circle" [size]="20" class="animate-spin mx-auto text-slate-blue" />
        <p class="text-[13px] mt-2 text-steel-gray">Cargando tu reserva&hellip;</p>
      </div>
    } @else {

      <section class="rounded-3xl border p-5 sm:p-6 border-platinum-tint bg-cloud-mist shadow-tfr-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-steel-gray">Resumen</p>
        <h2 class="text-[20px] font-bold tracking-tight mt-2 leading-tight text-midnight-indigo">
          {{ space()?.name ?? 'Espacio' }}
        </h2>

        <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <dt class="text-[10px] font-semibold uppercase tracking-wider text-steel-gray">Fecha</dt>
            <dd class="text-[14px] font-semibold mt-1 text-midnight-indigo">{{ formatDate(draft()?.date) }}</dd>
          </div>
          <div>
            <dt class="text-[10px] font-semibold uppercase tracking-wider text-steel-gray">Horario</dt>
            <dd class="text-[14px] font-semibold mt-1 tabular-nums text-midnight-indigo">
              {{ draft()?.startTime }} &ndash; {{ draft()?.endTime }}
              <span class="text-[12px] font-normal text-steel-gray">({{ durationHours() }} h)</span>
            </dd>
          </div>
        </dl>

        <div class="mt-5 pt-4 border-t border-pale-gray space-y-2.5">
          <div class="flex items-baseline justify-between gap-3">
            <span class="text-[13px] text-slate-blue">Precio total del alquiler (IVA incluido)</span>
            <span class="text-[15px] font-semibold tabular-nums text-midnight-indigo">{{ totalPrice() }}€</span>
          </div>
          @if (isStripeCheckout()) {
            <div class="flex items-baseline justify-between gap-3 pt-1 border-t border-pale-gray">
              <span class="text-[13px] font-medium text-midnight-indigo">A pagar ahora (tarjeta)</span>
              <span class="text-[15px] font-bold tabular-nums text-midnight-indigo">{{ payNowAmount() }}€</span>
            </div>
            <p class="text-[11px] text-steel-gray leading-relaxed">
              Incluye la tasa de gesti&oacute;n de la plataforma. La fianza, si aplica, se gestiona
              desde 48&nbsp;h antes del evento.
            </p>
          } @else {
            <div class="flex items-baseline justify-between gap-3">
              <span class="text-[13px] text-slate-blue">A pagar ahora &middot; Tasa de gesti&oacute;n online</span>
              <span class="text-[15px] font-semibold tabular-nums text-midnight-indigo">{{ platformFee() }}€</span>
            </div>
            <p class="text-[11px] text-steel-gray leading-relaxed">
              Calculada sobre el precio del alquiler; el importe final depende del perfil fiscal del espacio.
            </p>
            <div class="flex items-baseline justify-between gap-3 pt-1 border-t border-pale-gray">
              <span class="text-[13px] font-medium text-midnight-indigo">Saldo pendiente (Pago directo al propietario)</span>
              <span class="text-[15px] font-bold tabular-nums text-midnight-indigo">{{ pendingBalance() }}€</span>
            </div>
          }
        </div>
      </section>

      @if (checkoutNotice(); as notice) {
        <div class="rounded-xl px-3 py-2.5 border border-platinum-tint bg-cloud-mist">
          <p class="text-[12px] text-center text-slate-blue">{{ notice }}</p>
        </div>
      }

      <label class="flex items-start gap-3 rounded-2xl border border-platinum-tint bg-snow-white p-4 cursor-pointer select-none">
        <input
          type="checkbox"
          class="mt-0.5 h-4 w-4 shrink-0 rounded border-pale-gray text-midnight-indigo focus:ring-midnight-indigo/30"
          [checked]="conditionsAccepted()"
          (change)="conditionsAccepted.set($any($event.target).checked)" />
        <span class="text-[12px] font-bold leading-snug text-midnight-indigo">
          @if (isStripeCheckout()) {
            Acepto las Condiciones de Reserva y la Política de Cancelación. Entiendo que, si desisto antes de
            formalizar, se reembolsar&aacute; el alquiler pagado menos la comisi&oacute;n de gesti&oacute;n online.
          } @else {
            Acepto las Condiciones de Reserva de la plataforma y su Política de Cancelación. Entiendo que la tasa de
            gestión online se devenga de forma inmediata por el servicio técnico de bloqueo del calendario y no es
            reembolsable si decido desistir de la reserva.
          }
        </span>
      </label>

      @if (checkoutEmail(); as email) {
        <p class="rounded-xl border border-platinum-tint bg-cloud-mist px-3 py-2.5 text-[12px] leading-relaxed text-center text-steel-gray">
          <lucide-icon name="mail" [size]="13" class="inline-block mr-1 -mt-0.5 text-slate-blue" />
          Pago con <span class="font-semibold text-midnight-indigo">{{ email }}</span>.
          En Stripe este email aparecerá bloqueado y no podrás modificarlo.
        </p>
      } @else {
        <p class="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] text-center text-amber-900">
          Tu cuenta no tiene email verificado. No podrás continuar al pago hasta vincular uno en tu perfil de acceso.
        </p>
      }

      @if (payError(); as err) {
        <p class="text-[12px] text-red-600 text-center">{{ err }}</p>
      }

      <button
        type="button"
        (click)="redirectToCheckout()"
        [disabled]="!canConfirmPayment()"
        class="w-full py-3.5 rounded-2xl text-[15px] font-semibold text-snow-white bg-midnight-indigo
               transition-opacity disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none
               focus:ring-2 focus:ring-midnight-indigo/40 shadow-tfr-sm hover:brightness-110">
        @if (submitting()) {
          Redirigiendo a Stripe&hellip;
        } @else {
          Confirmar y Pagar {{ payNowAmount() }}€
        }
      </button>

      <p class="flex items-center justify-center gap-1.5 text-[11px] text-center leading-snug text-steel-gray">
        <lucide-icon name="shield-check" [size]="13" class="shrink-0" />
        <span>Tras el pago completarás el contrato con el propietario (Paso 2)</span>
      </p>

    }
  </div>
</div>
